import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import {
    FeedBadge,
    FeedCardHeader,
    FeedCreator,
    FeedDescription,
    FeedDetailMedia,
    FeedDetailSection,
    FeedIdentity,
    FeedInlineState,
    FeedMeta,
    FeedRetryButton,
    FeedSummaryButton,
    FeedSummaryRow,
    FeedSummaryText
} from "src/components/NoticeboardFeedUi";
import { StandardModal } from "src/components";
import { getNotice } from "../../api/feedApi";
import {
    getNoticeAudienceLabel,
    getNoticeAuthorIdentity,
    getNoticeCommentCount,
    mergeNoticeFeedUpdate
} from "../../feedUtils";
import NoticeboardCommentsPanel from "./NoticeboardCommentsPanel";
import NoticeboardFeedAttachments from "./NoticeboardFeedAttachments";
import NoticeboardFeedAvatar from "./NoticeboardFeedAvatar";
import NoticeboardFeedMedia from "./NoticeboardFeedMedia";
import { hasValidCheckIn } from "../../checkIn";
import { CheckInDisplay } from "../CheckInSection";

const NoticeboardPostDetailModal = ({
    open,
    onClose,
    token,
    notice,
    interactionUuid,
    employeeName,
    clientName,
    audience,
    showAudience,
    canUseCheckedIn,
    onOpenReactions,
    onCommentCreated
}) => {
    const [post, setPost] = useState(notice);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const loadPost = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const fetchedPost = await getNotice(token, notice.uuid);
            setPost(currentPost => mergeNoticeFeedUpdate(currentPost, fetchedPost));
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [notice.uuid, token]);

    useEffect(() => {
        if (open) {
            setPost(notice);
            loadPost();
        }
    }, [loadPost, notice, open]);

    const creatorName = post.postedBy || "Unavailable user";
    const creatorIdentity = getNoticeAuthorIdentity(post);
    const publishedDate = post.postedDate || post.created_at;
    const audienceLabel = showAudience ?
        getNoticeAudienceLabel(post, employeeName, clientName) :
        "";
    const commentCount = getNoticeCommentCount(post, audience);
    const showDiscussion = post.allowComments || commentCount > 0;
    const isCheckedIn = canUseCheckedIn && hasValidCheckIn(post.checkIn);

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            title={post.subject || "Noticeboard post"}
            description={showDiscussion ? "Post details and complete discussion." : "Post details."}
            widthMode="large"
            bodyPadding="0"
            bodyGap="0"
            paperStyle={{ maxHeight: "min(80rem, calc(100vh - 2rem))" }}
            disableEnforceFocus={false}
            disableScrollLock={false}
        >
            {error ? (
                <FeedInlineState $error role="alert">
                    This post may have been removed or is currently unavailable.
                    <div><FeedRetryButton type="button" onClick={loadPost}>Retry</FeedRetryButton></div>
                </FeedInlineState>
            ) : (
                <NoticeboardCommentsPanel
                    token={token}
                    noticeUuid={post.uuid}
                    interactionUuid={interactionUuid}
                    allowComments={post.allowComments}
                    showDiscussion={showDiscussion}
                    onCommentCreated={onCommentCreated}
                    postContent={(
                        <>
                            {loading && (
                                <FeedInlineState role="status">Refreshing post…</FeedInlineState>
                            )}
                            <FeedCardHeader $hasAvatar={Boolean(creatorIdentity.displayName)}>
                                <NoticeboardFeedAvatar
                                    displayName={creatorIdentity.displayName}
                                    profilePictureUrl={creatorIdentity.profilePictureUrl}
                                    size="2.75rem"
                                    fontSize="0.8rem"
                                />
                                <FeedIdentity>
                                    <FeedCreator>
                                        {isCheckedIn ? `${creatorName} Checked In to` : `Created by ${creatorName}`}
                                    </FeedCreator>
                                    <FeedMeta>
                                        <time dateTime={publishedDate}>
                                            {moment.utc(publishedDate).local().format("D MMM YYYY [at] h:mm A")}
                                        </time>
                                        {audienceLabel && <FeedBadge>{audienceLabel}</FeedBadge>}
                                        {post.isPinned && <FeedBadge $important>Pinned</FeedBadge>}
                                    </FeedMeta>
                                </FeedIdentity>
                            </FeedCardHeader>
                            {post.message && (
                                <FeedDetailSection $padding="0 1.15rem 1rem">
                                    <FeedDescription>{post.message}</FeedDescription>
                                </FeedDetailSection>
                            )}
                            {post.postType !== "text" && (
                                <FeedDetailMedia>
                                    <NoticeboardFeedMedia notice={post} modalMode initialIndex={0} />
                                </FeedDetailMedia>
                            )}
                            <NoticeboardFeedAttachments attachments={post.attachments} />
                            <CheckInDisplay checkIn={canUseCheckedIn ? post.checkIn : null} detail />
                            <FeedSummaryRow>
                                <FeedSummaryButton type="button" onClick={onOpenReactions}>
                                    {post.reactions.emojis.slice(0, 3)
                                        .map(reaction => reaction.unicode).join(" ")}
                                    {post.reactions.total}{" "}
                                    {post.reactions.total === 1 ? "reaction" : "reactions"}
                                </FeedSummaryButton>
                                <FeedSummaryText>
                                    {post.allowComments ?
                                        `${commentCount} ${commentCount === 1 ? "comment" : "comments"}` :
                                        "Comments are turned off for this post."
                                    }
                                </FeedSummaryText>
                            </FeedSummaryRow>
                        </>
                    )}
                />
            )}
        </StandardModal>
    );
};

export default NoticeboardPostDetailModal;
